import React, { useState, useRef, useEffect } from "react";
import Icon from "../AppIcon";

/**
 * Standardized pagination component used across all list views.
 * Default page size is 25 records.
 * Includes export functionality when handlers are provided.
 */
const Pagination = ({
  page = 1,
  pageSize = 25,
  totalCount = 0,
  totalPages = 0,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 25, 50, 100],
  // Export functionality
  onExportCurrentPage,
  onExportAll,
  exportDisabled = false,
  exportLoading = false,
  className = "",
}) => {
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsExportDropdownOpen(false);
      }
    };

    if (isExportDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExportDropdownOpen]);

  const hasExport = onExportCurrentPage || onExportAll;
  const hasMultiplePages = totalPages > 1;

  // If no records, show just the count with export if available
  if (totalCount === 0) {
    return (
      <div className={`flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border ${className}`}>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">0</span> results
        </p>
      </div>
    );
  }

  // If only one page and no pagination needed
  if (totalPages <= 1) {
    return (
      <div className={`flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border ${className}`}>
        <p className="text-sm text-muted-foreground">
<span className="font-medium">{totalCount.toLocaleString()}</span> results
        </p>

        {/* Simple export button for single page */}
        {hasExport && (
          <button
    className="btn btn-outline btn-sm"
          onClick={onExportAll || onExportCurrentPage}
 disabled={exportDisabled || exportLoading || totalCount === 0}
>
       {exportLoading ? (
<div className="spinner w-4 h-4" />
      ) : (
            <Icon name="Download" size={16} />
          )}
        <span className="ml-1">Export CSV</span>
   </button>
        )}
      </div>
    );
  }

  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalCount);

  // Calculate visible page numbers - compact: 1, 2, 3, ... last
  const getVisiblePages = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show page 1
      pages.push(1);

      if (page <= 3) {
        // Near the start: 1, 2, 3, ..., last
        pages.push(2, 3);
      pages.push("...");
        pages.push(totalPages);
   } else if (page >= totalPages - 2) {
   // Near the end: 1, ..., n-2, n-1, n
        pages.push("...");
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle: 1, ..., p, ..., last
        pages.push("...");
        pages.push(page);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleExportCurrentPage = () => {
    onExportCurrentPage?.();
    setIsExportDropdownOpen(false);
  };

  const handleExportAll = () => {
    onExportAll?.();
    setIsExportDropdownOpen(false);
  };

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg border border-border ${className}`}
    >
   {/* Results info and page size selector */}
      <div className="flex items-center gap-4">
        <p className="text-sm text-muted-foreground">
     Showing <span className="font-medium">{startRecord}</span> to{" "}
          <span className="font-medium">{endRecord}</span> of{" "}
 <span className="font-medium">{totalCount.toLocaleString()}</span>{" "}
          results
        </p>

        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show:</span>
     <select
              value={pageSize}
  onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
     className="input input-sm w-20"
     >
      {pageSizeOptions.map((size) => (
     <option key={size} value={size}>
   {size}
                </option>
      ))}
 </select>
  </div>
        )}
      </div>

      {/* Page navigation and export */}
      <div className="flex items-center gap-3">
        {/* Page navigation */}
        {totalPages > 1 && (
        <div className="flex items-center gap-1">
      {/* First page */}
      <button
          onClick={() => onPageChange(1)}
  disabled={page === 1}
 className="btn btn-ghost btn-sm p-2"
     title="First page"
      >
      <Icon name="ChevronsLeft" size={16} />
       </button>

{/* Previous page */}
            <button
          onClick={() => onPageChange(page - 1)}
      disabled={page === 1}
         className="btn btn-ghost btn-sm"
            >
    <Icon name="ChevronLeft" size={16} />
         <span className="hidden sm:inline">Previous</span>
      </button>

            {/* Page numbers */}
   <div className="hidden md:flex items-center gap-1">
 {getVisiblePages().map((pageNum, idx) =>
            pageNum === "..." ? (
      <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground select-none">…</span>
 ) : (
          <button
         key={pageNum}
           onClick={() => onPageChange(pageNum)}
              className={`btn btn-sm w-10 ${
       pageNum === page ? "btn-primary" : "btn-ghost"
}`}
      >
{pageNum}
</button>
    )
)}
     </div>

{/* Mobile page indicator */}
       <span className="md:hidden text-sm text-muted-foreground px-2">
              Page {page} of {totalPages}
       </span>

            {/* Next page */}
    <button
       onClick={() => onPageChange(page + 1)}
 disabled={page >= totalPages}
      className="btn btn-ghost btn-sm"
   >
    <span className="hidden sm:inline">Next</span>
    <Icon name="ChevronRight" size={16} />
            </button>

            {/* Last page */}
            <button
              onClick={() => onPageChange(totalPages)}
     disabled={page >= totalPages}
              className="btn btn-ghost btn-sm p-2"
       title="Last page"
      >
              <Icon name="ChevronsRight" size={16} />
        </button>
          </div>
        )}

  {/* Export dropdown for multiple pages */}
        {hasExport && hasMultiplePages && (
  <div className="relative" ref={dropdownRef}>
  <button
        className="btn btn-outline btn-sm"
  onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
       disabled={exportDisabled || exportLoading || totalCount === 0}
     >
   {exportLoading ? (
     <div className="spinner w-4 h-4" />
       ) : (
       <Icon name="Download" size={16} />
       )}
            <span className="hidden sm:inline ml-1">Export CSV</span>
     <Icon name="ChevronDown" size={14} className="ml-1" />
        </button>

       {isExportDropdownOpen && (
              <div className="absolute right-0 bottom-full mb-1 bg-card rounded-lg shadow-lg border border-border p-1 w-52 z-50">
    {onExportCurrentPage && (
<button
   onClick={handleExportCurrentPage}
   className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-md w-full text-left"
 >
         <Icon name="FileSpreadsheet" size={16} />
    Export Current Page
      </button>
     )}
         {onExportAll && (
          <button
     onClick={handleExportAll}
 className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-md w-full text-left"
           >
             <Icon name="Files" size={16} />
     Export All ({totalCount.toLocaleString()})
       </button>
                )}
           </div>
            )}
        </div>
        )}
</div>
    </div>
  );
};

export default Pagination;
